"use client";

import {
  HistorySnapshot,
  MultiplayerPatchMetadata,
  MultiplayerStateManager,
} from "@noya-app/state-manager";
import React, { useEffect, useRef } from "react";
import { ObjectInspector } from "react-inspector";
import { StateInspectorButton } from "../StateInspectorButton";
import {
  StateInspectorDisclosureRowInner,
  StateInspectorDisclosureSection,
} from "../StateInspectorDisclosureSection";
import {
  getStateInspectorBorderColor,
  getStateInspectorTheme,
} from "../inspectorTheme";
import { pathToString } from "../utils";

const HISTORY_ELEMENT_PREFIX = "noya-multiplayer-history-";

export function HistorySection<S, M extends object>({
  showHistory,
  setShowHistory,
  colorScheme,
  historySnapshot,
  multiplayerStateManager,
}: {
  showHistory: boolean;
  setShowHistory: (value: boolean) => void;
  colorScheme: "light" | "dark";
  historySnapshot: HistorySnapshot<S, Partial<M> & MultiplayerPatchMetadata>;
  multiplayerStateManager: MultiplayerStateManager<S, M>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const theme = getStateInspectorTheme(colorScheme);
  const solidBorderColor = getStateInspectorBorderColor(colorScheme);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [historySnapshot]);

  // If history index changes, scroll to the new history entry into view
  useEffect(() => {
    if (!ref.current) return;

    const historyEntry = ref.current.querySelector(
      `#${HISTORY_ELEMENT_PREFIX}${historySnapshot.historyIndex}`
    );

    if (historyEntry) {
      historyEntry.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [historySnapshot.historyIndex]);

  return (
    <StateInspectorDisclosureSection
      open={showHistory}
      setOpen={setShowHistory}
      title="History"
      colorScheme={colorScheme}
      right={
        <span
          style={{
            display: "flex",
            gap: "4px",
          }}
        >
          <StateInspectorButton
            disabled={!historySnapshot.canUndo}
            theme={theme}
            onClick={() => {
              multiplayerStateManager.undo();
            }}
          >
            <span style={{ width: "12px", height: "12px" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 20 20"
              >
                <path
                  fill="currentColor"
                  d="M12 5H7V2L1 6l6 4V7h5c2.2 0 4 1.8 4 4s-1.8 4-4 4H7v2h5c3.3 0 6-2.7 6-6s-2.7-6-6-6"
                />
              </svg>
            </span>
          </StateInspectorButton>
          <StateInspectorButton
            disabled={!historySnapshot.canRedo}
            theme={theme}
            onClick={() => {
              multiplayerStateManager.redo();
            }}
          >
            <span style={{ width: "12px", height: "12px" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 20 20"
              >
                <path
                  fill="currentColor"
                  d="M8 5h5V2l6 4l-6 4V7H8c-2.2 0-4 1.8-4 4s1.8 4 4 4h5v2H8c-3.3 0-6-2.7-6-6s2.7-6 6-6"
                />
              </svg>
            </span>
          </StateInspectorButton>
        </span>
      }
    >
      <StateInspectorDisclosureRowInner ref={ref}>
        <div
          id={`${HISTORY_ELEMENT_PREFIX}0`}
          style={{
            borderBottom: `1px solid ${solidBorderColor}`,
            fontSize: "12px",
            fontFamily: "Menlo, monospace",
            padding: "2px 12px 1px",
            display: "flex",
            alignItems: "center",
            background:
              historySnapshot.historyIndex === 0
                ? "rgb(59 130 246 / 15%)"
                : undefined,
          }}
        >
          <span
            style={{
              fontFamily: "Menlo, monospace",
              fontSize: "11px",
              borderRadius: 4,
              display: "inline-block",
            }}
          >
            Initial state
          </span>
        </div>
        {historySnapshot.history.map((entry, index) => {
          const metadata = entry.metadata;
          const { id, name, timestamp, ...rest } = metadata;

          return (
            <div
              id={`${HISTORY_ELEMENT_PREFIX}${index + 1}`}
              key={index}
              style={{
                padding: "0px 12px 1px",
                borderBottom: `1px solid ${solidBorderColor}`,
                background:
                  index + 1 === historySnapshot.historyIndex
                    ? "rgb(59 130 246 / 15%)"
                    : undefined,
              }}
            >
              {typeof name === "string" && (
                <pre
                  style={{
                    fontSize: "11px",
                    display: "flex",
                    flexWrap: "wrap",
                    margin: 0,
                  }}
                >
                  {entry.metadata.name}
                  {Object.keys(rest).length > 0 && (
                    <ObjectInspector data={rest} theme={theme} />
                  )}
                </pre>
              )}
              {entry.redoPatches?.map((patch, j) => (
                <pre
                  key={j}
                  style={{
                    fontSize: "11px",
                    display: "flex",
                    flexWrap: "wrap",
                    margin: 0,
                  }}
                >
                  {patch.op === "add" && (
                    <>
                      <span style={{ fontStyle: "italic" }}>
                        {pathToString(patch.path)}
                      </span>
                      {" = "}
                      <ObjectInspector data={patch.value} theme={theme} />
                    </>
                  )}
                  {patch.op === "replace" && (
                    <>
                      <span style={{ fontStyle: "italic" }}>
                        {pathToString(patch.path)}
                      </span>
                      {" = "}
                      <ObjectInspector data={patch.value} theme={theme} />
                    </>
                  )}
                  {patch.op === "remove" && (
                    <>
                      <span style={{ color: theme.OBJECT_VALUE_STRING_COLOR }}>
                        delete{" "}
                      </span>
                      <span style={{ fontStyle: "italic" }}>
                        {pathToString(patch.path)}
                      </span>
                    </>
                  )}
                  {patch.op === "move" && (
                    <>
                      <span style={{ fontStyle: "italic" }}>
                        {pathToString(patch.from!)}
                      </span>
                      {" -> "}
                      <span style={{ fontStyle: "italic" }}>
                        {pathToString(patch.path)}
                      </span>
                    </>
                  )}
                </pre>
              ))}
            </div>
          );
        })}
      </StateInspectorDisclosureRowInner>
    </StateInspectorDisclosureSection>
  );
}
